import { LoadScreen } from "../helpers/loadscreen";

interface LoadingTask {
    name: string;
    loader: (onProgress: (progress: number) => void) => Promise<void>;
}

/**
 * The base class for all states within the application.
 */
export class State {
    private _loadingTasks: LoadingTask[];
    private _isExecuting: boolean;

    constructor() {
        this._loadingTasks = [];
        this._isExecuting = false;
    }

    /**
     * Preparation handler called once per application.
     * Override to load one-time resources.
     */
    async prepareOnce(): Promise<void> {
        // Default implementation does nothing
    }

    /**
     * Preparation handler called prior to every entry into the state.
     * This now supports loading tasks with progress tracking.
     * Override generateLoadingTasks() to define what to load.
     */
    async prepare(): Promise<void> {
        this._loadingTasks = this.generateLoadingTasks();

        if (this._loadingTasks.length === 0) {
            // No loading tasks, resolve immediately
            return;
        }

        // Has loading tasks, show loading screen and execute them
        LoadScreen.show();
        LoadScreen.setMessage("Loading...");
        LoadScreen.setProgress(0);
        this._isExecuting = true;

        try {
            await this._executeAllTasks();

            // All tasks complete
            LoadScreen.setProgress(100);
            LoadScreen.setMessage("Complete!");

            // Wait a bit before hiding the screen
            await new Promise<void>((resolve) => {
                setTimeout(() => {
                    if (this._isExecuting) {
                        LoadScreen.hide();
                    }
                    resolve();
                }, 200);
            });
        } finally {
            this._isExecuting = false;
        }
    }

    /**
     * Override this method to define loading tasks for your state.
     * Return an empty array for states that don't need loading.
     */
    protected generateLoadingTasks(): LoadingTask[] {
        return [];
    }

    private async _executeAllTasks(): Promise<void> {
        for (let i = 0; i < this._loadingTasks.length; i++) {
            // Check if we should stop executing (state was left)
            if (!this._isExecuting) {
                throw new Error('Loading interrupted');
            }

            const task = this._loadingTasks[i];
            const taskStartProgress = (i / this._loadingTasks.length) * 100;
            const taskProgressRange = 100 / this._loadingTasks.length;

            LoadScreen.setMessage(task.name);

            await task.loader((taskProgress: number) => {
                // Only update progress if still executing
                if (!this._isExecuting) {
                    return;
                }
                // Task progress is 0-100, map it to this task's range
                const overallProgress = taskStartProgress + (taskProgress / 100) * taskProgressRange;
                LoadScreen.setProgress(overallProgress);
            });
        }
    }

    /**
     * Called when the state is being entered.
     */
    enter() {
    }

    /**
     * Called when the state is being left.
     */
    leave() {
        // Stop any ongoing loading task execution
        this._isExecuting = false;
        this._loadingTasks = [];
    }

    /**
     * Called every frame while the state is active.
     */
    update(delta: any) {
    }
}

